// Display density (UX spec 3.5): Comfortable, the default, or Compact, chosen per user in Settings.
// Kept in this browser beside the theme (lib/theme.ts), keyed by the signed-in user, and applied as
// html[data-density]. styles/tokens.css does the rest: compact changes spacing tokens only, never
// colour, type, controls, the masthead or the status bar.
export type Density = 'comfortable' | 'compact';

const PREFIX = 'sidetone.density.';
export const densityKey = (userId: string): string => `${PREFIX}${userId}`;

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const listeners = new Set<() => void>();
// The choice still applies to this page when storage is unavailable.
const memory = new Map<string, Density>();

function browserStore(): Store | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readDensity(userId: string, store: Store | null = browserStore()): Density {
  const remembered = memory.get(userId);
  if (remembered) return remembered;
  try {
    return store?.getItem(densityKey(userId)) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

export function writeDensity(userId: string, density: Density, store: Store | null = browserStore()): void {
  memory.set(userId, density);
  try {
    if (density === 'compact') store?.setItem(densityKey(userId), 'compact');
    else store?.removeItem(densityKey(userId));
  } catch {
    // Storage unavailable: the in-memory choice applies until the page reloads.
  }
  for (const listener of listeners) listener();
}

/** Changes from this tab and, through the storage event, from the user's other tabs. */
export function subscribeDensity(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (!event.key?.startsWith(PREFIX)) return;
    memory.delete(event.key.slice(PREFIX.length));
    listener();
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

export function applyDensity(density: Density | null, root: { dataset: DOMStringMap } | null = typeof document === 'undefined' ? null : document.documentElement): void {
  if (!root) return;
  if (density) root.dataset.density = density;
  else delete root.dataset.density;
}
